import { HipaaLogo } from "~/icons";
import { ROUTES } from "~/router/routes";
import { ArrowIcon } from "~/shared/components/icons/ArrowIcon";
import type { FormRegistryEntry } from "~/shared/utils/formRegistry";
import { validateStepOrder } from "~/shared/utils/formRegistry";
import {
  EmailForm,
  FormActionButtonsContainer,
  FormContentContainer,
  FullNameForm,
  HealthConditionsForm,
  LocationForm,
  OtherInfoForm,
  PhoneForm,
  PrimaryInsuranceProviderForm,
  ReferredByForm,
  SecondaryInsuranceProviderForm,
} from "../components/SignUp";
import { SignUpFlowShell } from "../components/SignUp/SignUpFlowShell";
import { shouldFirePixelEvents, trackGA4Lead } from "../utils";
import { FLOW_VERSION } from "../utils/flowVersion";

const formSteps: FormRegistryEntry[] = [
  {
    component: HealthConditionsForm,
    id: "health_conditions",
    writes: ["conditions"],
    reads: [],
  },
  {
    component: FullNameForm,
    id: "full_name",
    writes: ["firstName", "lastName"],
    reads: [],
  },
  {
    component: PrimaryInsuranceProviderForm,
    id: "primary_insurance",
    writes: ["primaryInsurance"],
    reads: ["state"],
  },
  {
    component: SecondaryInsuranceProviderForm,
    id: "secondary_insurance",
    writes: ["secondaryInsurance"],
    reads: ["state"],
  },
  {
    component: LocationForm,
    id: "location",
    writes: ["state", "visitType", "preferredOfficeVisit"],
    reads: [],
  },
  { component: EmailForm, id: "email", writes: ["email"], reads: [] },
  { component: PhoneForm, id: "phone", writes: ["phone"], reads: [] },
  {
    component: ReferredByForm,
    id: "referred_by",
    writes: ["referral", "otherReferral"],
    reads: [],
  },
  {
    component: OtherInfoForm,
    id: "other_info",
    writes: ["extraInformation"],
    reads: [],
  },
];

if (import.meta.env.DEV) {
  const warnings = validateStepOrder(formSteps);
  if (warnings.length > 0) {
    console.warn("[FormRegistry] Step order warnings:", warnings);
  }
}

export const SignUp = () => (
  <SignUpFlowShell
    formSteps={formSteps}
    flowVersion={FLOW_VERSION}
    terminalRoute={ROUTES.providerSelection}
    onFirstPageView={() => {
      if (shouldFirePixelEvents()) {
        trackGA4Lead();
      }
    }}
    renderIntro={(onStart) => (
      <div className="flex min-h-[calc(100dvh-2rem)] flex-col rounded-lg shadow-xl sm:min-h-0">
        <FormContentContainer noPadding>
          <div className="flex flex-col items-start gap-4 px-6 pt-6 pb-6 sm:px-10 sm:pt-8 sm:pb-6">
            <HipaaLogo className="size-16 self-center sm:size-20" />
            <img
              src="/images/sign-up-family.png"
              className="max-h-[30dvh] w-full rounded-lg object-cover sm:max-h-none"
              alt=""
            />
            <h1 className="font-display sm:text-large-title text-2xl">
              Embark on the journey to a healthier, happier you.
            </h1>
            <span className="font-roboto text-base sm:text-lg">
              In the upcoming moments, we'll guide you in discovering the ideal
              dietitian tailored to your individualized needs, lifestyle, and
              objectives.
            </span>
          </div>
        </FormContentContainer>
        <div className="mt-auto">
          <FormActionButtonsContainer>
            <button
              className="flex cursor-pointer items-center justify-end gap-2 bg-green-300 px-6 py-4 font-medium"
              onClick={onStart}
            >
              <span>GET STARTED</span>
              <ArrowIcon direction="left" />
            </button>
          </FormActionButtonsContainer>
        </div>
      </div>
    )}
  />
);
